<template>
  <div class="admin-page admin-page-panel" :class="{ 'admin-page-embedded': embedded }">
    <template v-if="!dialogOnly">
    <div class="admin-filter-bar">
      <a-select v-if="!embedded" v-model="query.eventId" allow-clear placeholder="选择赛事" style="width:220px; margin-right:10px;" @change="handleEventChange">
        <a-select-option v-for="item in eventOptions" :key="item.id" :value="item.id">{{ item.title }}</a-select-option>
      </a-select>
      <a-select v-model="query.categoryId" allow-clear placeholder="选择组别" style="width:200px; margin-right:10px;">
        <a-select-option v-for="item in categoryOptions" :key="item.id" :value="item.id">{{ item.categoryName }}</a-select-option>
      </a-select>
      <a-select v-model="query.auditStatus" allow-clear placeholder="审核状态" style="width:160px; margin-right:10px;">
        <a-select-option :value="0">待审核</a-select-option>
        <a-select-option :value="1">已通过</a-select-option>
        <a-select-option :value="2">已拒绝</a-select-option>
      </a-select>
      <a-button type="primary" icon="search" @click="loadData">查询</a-button>
    </div>

    <a-table :columns="columns" :data-source="data" rowKey="id" :loading="loading" :pagination="pagination" @change="handleTableChange">
      <span slot="auditStatus" slot-scope="text">
        <a-tag :color="statusMap[text] && statusMap[text].color">{{ statusMap[text] ? statusMap[text].label : '未知' }}</a-tag>
      </span>
      <template slot="teamPhoto" slot-scope="text, record">
        <a v-if="record.teamPhotoUrl" @click="previewPhoto(record.teamPhotoUrl)">查看</a>
        <span v-else style="color:#999;">—</span>
      </template>
      <span class="admin-row-actions" slot="action" slot-scope="text, record">
        <a @click="openAudit(record)">审核</a>
        <a-divider type="vertical" />
        <a-popconfirm title="确定删除该队伍报名吗？相关成绩也会一并删除。" @confirm="handleDelete(record.id)">
          <a style="color:red;">删除</a>
        </a-popconfirm>
      </span>
    </a-table>
    </template>

    <a-modal v-model="visible" title="审核报名" dialog-class="registration-audit-modal" width="1160" @ok="submitAudit">
      <div v-if="auditForm.auditStatus === 1" class="registration-approved-note" style="padding:10px 14px; margin-bottom:12px; border-radius:8px; background:var(--jk-warning-bg, #FFF7E6); color:var(--jk-warning-text, #B8860B); font-size:13px;">
        报名已审核通过：仅可调整已报名成员的座位与资料，不可新增队员。
      </div>
      <div class="audit-layout">
        <!-- 左侧：队员资料 -->
        <div class="audit-left">
          <div class="audit-panel-title">
            <span>队员资料</span>
            <span class="audit-panel-count">座舱 {{ auditForm.seatMembers.length }} 人 · 替补 {{ auditForm.alternateMembers.length }} 人</span>
          </div>
          <div class="audit-member-add" style="margin:10px 0; display:flex; gap:8px; align-items:center;">
            <a-select v-model="memberAddSelection" allow-clear placeholder="从本次已报名人员安排" style="width:200px;" :loading="teamMemberLoading">
              <a-select-option v-for="m in teamMemberOptions.filter(m => isInSnapshot(m.id) && Number(m.isLeader) !== 1 && m.racePosition !== '教练')" :key="m.id" :value="m.id">{{ m.memberName }}{{ m.racePosition ? '（' + m.racePosition + '）' : '' }}</a-select-option>
            </a-select>
            <a-radio-group v-model="memberAddIsAlternate" size="small">
              <a-radio-button :value="0">座舱</a-radio-button>
              <a-radio-button :value="1" :disabled="!unlimitedAlternates && (auditForm.alternateMembers || []).length >= maxAlternates">替补</a-radio-button>
            </a-radio-group>
            <a-button size="small" type="primary" :disabled="!memberAddSelection" @click="submitMemberAdd">添加</a-button>
          </div>
          <div class="audit-member-list">
            <div class="audit-member-card" v-for="item in membersWithStatus()" :key="'m' + item.id"
              :class="item._statusType === 'idle' ? 'audit-alt' : item._statusType === 'foreign' ? 'audit-foreign' : ''"
              :draggable="item._statusType !== 'foreign'" @dragstart="onMemberDragStart($event, item)">
              <div class="audit-member-head">
                <span class="audit-member-name">{{ item.memberName }}
                  <a-tag v-if="item.isLeader === 1" color="cyan">领队</a-tag>
                  <a-tag :color="item._statusType === 'seat' ? 'blue' : item._statusType === 'alt' ? 'orange' : item._statusType === 'foreign' ? 'gray' : 'default'">{{ item._status }}</a-tag>
                </span>
                <span v-if="item._snapshot">
                  <a style="margin-left:8px;" @click="openMemberEdit(item._snapshot)">编辑</a>
                  <a-divider type="vertical" />
                  <a style="color:#FF4D4F;" @click="handleMemberRemove(item._snapshot)">移除</a>
                </span>
              </div>
              <div class="audit-member-fields">
                <span v-if="item.gender">性别：{{ item.gender }}</span>
                <span v-if="item.birthYear">出生：{{ item.birthYear }}年</span>
                <span v-if="item.heightCm">身高：{{ item.heightCm }}cm</span>
                <span v-if="item.weightKg">体重：{{ item.weightKg }}kg</span>
                <span v-if="item.mobile">手机：{{ item.mobile }}</span>
                <span v-if="item.idCardNo">身份证：{{ item.idCardNo }}</span>
              </div>
            </div>
            <div v-if="!membersWithStatus().length" style="color:#999; padding:20px; text-align:center;">该队伍暂无已通过成员</div>
          </div>
        </div>

        <!-- 右侧：座位图 + 审核 -->
        <div class="audit-right">
          <div class="audit-panel-title">队伍信息与座位安排</div>
          <div class="audit-team-info">
            <span><strong>{{ auditForm.teamName }}</strong></span>
            <a-tag v-if="auditForm.categoryName" color="green">{{ auditForm.categoryName }}</a-tag>
            <a-tag :color="teamAuditTagColor" v-if="auditForm.teamAuditStatus !== null && auditForm.teamAuditStatus !== undefined">{{ teamAuditTagText }}</a-tag>
          </div>
          <div v-if="auditForm.teamAuditStatus === 0" class="audit-team-warn">
            <span>该队伍（俱乐部）尚未通过审核</span>
            <a-button size="small" type="primary" :loading="teamAuditing" @click="auditTeamPass">通过队伍</a-button>
          </div>
          <div v-else-if="auditForm.teamAuditStatus === 2" class="audit-team-warn">
            <span style="color:#FF4D4F;">该队伍（俱乐部）审核被拒绝</span>
            <a-button size="small" type="primary" :loading="teamAuditing" @click="auditTeamPass">重新通过</a-button>
          </div>
            <img v-if="auditForm.teamPhotoUrl" :src="auditForm.teamPhotoUrl" class="audit-team-photo" alt="团队合照" />
          </div>
          <div class="audit-boat">
            <div class="audit-staff-title">领队 / 教练（随队人员，不占座舱、不占替补名额）</div>
            <div class="audit-staff-slots">
              <div class="audit-staff-slot" @click="staffSlotClick('leader')" @dragover.prevent="dragOverStaff = 'leader'" @dragleave="dragOverStaff = null" @drop.prevent="onStaffDrop($event, 'leader')" :class="{ 'audit-staff-slot-empty': !staffLeader(), dragover: dragOverStaff === 'leader' }">
                <div class="audit-staff-slot-label">领队</div>
                <div class="audit-staff-slot-body">
                  <template v-if="staffLeader()">
                    <span>{{ staffLeader().memberName }}</span>
                    <a style="color:#FF4D4F;" @click.stop="handleMemberRemove(staffLeader())">移除</a>
                  </template>
                  <span v-else class="audit-staff-slot-placeholder">点击选择</span>
                </div>
              </div>
              <div class="audit-staff-slot" @click="staffSlotClick('coach')" @dragover.prevent="dragOverStaff = 'coach'" @dragleave="dragOverStaff = null" @drop.prevent="onStaffDrop($event, 'coach')" :class="{ 'audit-staff-slot-empty': !staffCoach(), dragover: dragOverStaff === 'coach' }">
                <div class="audit-staff-slot-label">教练</div>
                <div class="audit-staff-slot-body">
                  <template v-if="staffCoach()">
                    <span>{{ staffCoach().memberName }}</span>
                    <a style="color:#FF4D4F;" @click.stop="handleMemberRemove(staffCoach())">移除</a>
                  </template>
                  <span v-else class="audit-staff-slot-placeholder">点击选择</span>
                </div>
              </div>
            </div>
            <div v-if="otherStaffMembers().length" class="audit-staff">
              <div class="audit-staff-item" v-for="s in otherStaffMembers()" :key="'st' + s.id">
                <span>{{ s.memberName }}<a-tag v-if="s.isLeader === 1" color="cyan" style="margin-left:6px;">领队</a-tag><a-tag v-else color="purple" style="margin-left:6px;">{{ s.racePosition || '随队' }}</a-tag></span>
                <a style="color:#FF4D4F;" @click="handleMemberRemove(s)">移除</a>
              </div>
            </div>
            <div class="audit-boat-end">船头</div>
            <div class="audit-boat-row" v-for="row in auditBoatRows()" :key="row.rowNo" :class="row.seats.length === 1 ? 'single' : 'double'">
              <div class="audit-boat-seat" v-for="seat in row.seats" :key="seat.seatOrder" @click="seatSelect(seat)" :class="{ empty: !seat.member, dragover: dragOverSeat === seat.seatOrder }"
                @dragover.prevent="dragOverSeat = seat.seatOrder" @dragleave="dragOverSeat = null" @drop.prevent="onSeatDrop($event, seat)">
                <span class="audit-boat-pos">{{ seat.seatOrder }}号 · {{ seat.sideText }}</span>
                <span class="audit-boat-name">{{ seat.member ? seat.member.memberName : '空位' }}<a-tag v-if="seat.member && seat.member.isLeader === 1" color="cyan" style="margin-left:6px;">领队</a-tag></span>
              </div>
            </div>
            <div class="audit-boat-end">船尾</div>
            <div class="audit-boat-alt">
              <div class="audit-boat-alt-title">替补（{{ unlimitedAlternates ? '不限额' : '最多 ' + maxAlternates + ' 人' }}）</div>
              <div class="audit-boat-alt-grid">
                <div class="audit-boat-alt-slot" v-for="slot in alternateSlots()" :key="'alts' + slot.slotNo"
                  @click="altSlotClick(slot)"
                  @dragover.prevent="dragOverAlt = slot.slotNo" @dragleave="dragOverAlt = null" @drop.prevent="onAltDrop($event)"
                  :class="{ 'audit-boat-alt-slot-empty': !slot.member, dragover: dragOverAlt === slot.slotNo }">
                  <div class="audit-boat-alt-slot-label">替补{{ slot.slotNo }}</div>
                  <div class="audit-boat-alt-slot-body">
                    <template v-if="slot.member">
                      <span>{{ slot.member.memberName }}</span>
                      <a style="color:#FF4D4F;" @click.stop="handleMemberRemove(slot.member)">移除</a>
                    </template>
                    <span v-else class="audit-staff-slot-placeholder">点击选择</span>
                  </div>
                </div>
              </div>
            </div>
            <div style="margin-top:10px; color:#999; font-size:12px;">点击空座位、替补或随队人员格子可选择队员；目标已有人时需先移除。电脑端也可拖拽队员卡片操作。</div>
          </div>
          <div class="audit-form">
            <div class="audit-form-row">
              <span class="audit-form-label">参赛编号</span>
              <a-input v-model="auditForm.bibNo" placeholder="可在审核时分配" style="flex:1;" />
            </div>
            <div class="audit-form-row">
              <span class="audit-form-label">审核结果</span>
              <a-radio-group v-model="auditForm.auditStatus">
                <a-radio :value="1">通过</a-radio>
                <a-radio :value="2">拒绝</a-radio>
              </a-radio-group>
            </div>
            <div class="audit-form-row">
              <span class="audit-form-label">审核备注</span>
              <a-textarea v-model="auditForm.auditRemark" :rows="2" style="flex:1;" />
            </div>
          </div>
        </div>
    </a-modal>

    <a-modal v-model="seatPickVisible" :title="seatPickTitle" dialog-class="registration-seat-picker" :footer="null" width="480">
      <div v-if="seatPickMembers().length" style="max-height:260px; overflow-y:auto;">
        <div v-for="m in seatPickMembers()" :key="m.id"
          style="display:flex; justify-content:space-between; align-items:center; padding:10px 8px; border-bottom:1px solid #f0f0f0; cursor:pointer;"
          :style="(m._used || (!m._inSnapshot)) ? 'color:#bbb; cursor:not-allowed;' : ''"
          @click="(m._used || (!m._inSnapshot)) ? null : seatPickMember(m)">
          <span>{{ m.memberName }}{{ m.racePosition ? '（' + m.racePosition + '）' : '' }}</span>
          <span v-if="m._used" style="font-size:12px;">已占用</span>
          <span v-else-if="!m._inSnapshot" style="font-size:12px;">未报名</span>
          <a v-else style="color:#4ECDC4;">上座 ›</a>
        </div>
      </div>
      <div v-else style="padding:20px 0; text-align:center; color:#999;">该队伍暂无已通过成员</div>
    </a-modal>

    <a-modal v-model="memberVisible" title="队员资料" width="640" @ok="submitMemberEdit">
      <a-form-model :model="memberForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-row>
          <a-col :span="12">
            <a-form-model-item label="姓名"><a-input v-model="memberForm.memberName" /></a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item label="性别">
              <a-select v-model="memberForm.gender" allow-clear placeholder="选择性别">
                <a-select-option value="男">男</a-select-option>
                <a-select-option value="女">女</a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
        </a-row>
        <a-row>
          <a-col :span="12">
            <a-form-model-item label="手机号"><a-input v-model="memberForm.mobile" /></a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item label="出生年份"><a-input-number v-model="memberForm.birthYear" :min="1930" :max="2026" style="width:100%;" /></a-form-model-item>
          </a-col>
        </a-row>
        <a-form-model-item label="身份证号"><a-input v-model="memberForm.idCardNo" /></a-form-model-item>
        <a-row>
          <a-col :span="12">
            <a-form-model-item label="身高(cm)"><a-input-number v-model="memberForm.heightCm" :min="100" :max="250" style="width:100%;" /></a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item label="体重(kg)"><a-input-number v-model="memberForm.weightKg" :min="30" :max="200" style="width:100%;" /></a-form-model-item>
          </a-col>
        </a-row>
        <a-row>
          <a-col :span="12">
            <a-form-model-item label="服装尺码"><a-input v-model="memberForm.clothingSize" placeholder="如 S/M/L/XL" /></a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item label="居住地"><a-input v-model="memberForm.residenceCity" placeholder="省市区" /></a-form-model-item>
          </a-col>
        </a-row>
        <a-form-model-item label="参赛经历">
          <a-textarea v-model="memberForm.participationHistory" :rows="3" placeholder="过往参赛及成绩" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import { addDragonRegistrationMember, auditDragonRegistration, auditDragonTeam, deleteDragonRegistration, getDragonCategoryList, getDragonEventList, getDragonRegistrationList, removeDragonRegistrationMember, updateDragonRegistrationMember } from '@/api'
import { formatDateTime } from '@/utils/date'

export default {
  name: 'DragonRegistration',
  props: {
    dialogOnly: { type: Boolean, default: false },
    eventId: {
      type: Number,
      default: undefined
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      query: { eventId: undefined, categoryId: undefined, auditStatus: undefined },
      eventOptions: [],
      categoryOptions: [],
      data: [],
      loading: false,
      visible: false,
      memberVisible: false,
      memberForm: {},
      memberAddSelection: undefined,
      memberAddIsAlternate: 0,
      teamMemberOptions: [],
      teamMemberLoading: false,
      boatRows: [],
      maxAlternates: 0,
      unlimitedAlternates: false,
      dragMemberId: null,
      dragMemberName: '',
      dragOverSeat: null,
      dragOverAlt: null,
      dragOverStaff: null,
      seatPickVisible: false,
      seatPickMode: 'seat',
      seatPickOrder: null,
      seatPickStaffRole: '',
      auditForm: {
        auditStatus: 1,
        teamName: '',
        teamPhotoUrl: '',
        bibNo: '',
        auditRemark: '',
        seatMembers: [],
        alternateMembers: [],
        staffMembers: [],
        teamAuditStatus: undefined,
        teamAuditRemark: ''
      },
      teamAuditing: false,
      pagination: { current: 1, pageSize: 10, total: 0 },
      statusMap: {
        0: { label: '待审核', color: 'orange' },
        1: { label: '已通过', color: 'green' },
        2: { label: '已拒绝', color: 'red' }
      },
      columns: [
        { title: '赛事', dataIndex: 'eventTitle' },
        { title: '组别', dataIndex: 'categoryName' },
        { title: '队伍', dataIndex: 'teamName' },
        { title: '编号', dataIndex: 'bibNo', width: 100 },
        { title: '团队照', key: 'teamPhotoUrl', width: 80, scopedSlots: { customRender: 'teamPhoto' } },
        { title: '提交时间', dataIndex: 'submittedAt', customRender: text => formatDateTime(text) },
        { title: '状态', dataIndex: 'auditStatus', width: 100, scopedSlots: { customRender: 'auditStatus' } },
        { title: '备注', dataIndex: 'auditRemark' },
        { title: '操作', key: 'action', width: 130, scopedSlots: { customRender: 'action' } }
      ]
    }
  },
  created() {
    if (!this.dialogOnly) this.loadEvents()
    if (this.eventId) {
      this.query.eventId = this.eventId
      this.loadCategories(this.eventId)
    }
    if (!this.dialogOnly) this.loadData()
  },
  watch: {
    eventId(val) {
      if (val) {
        this.query.eventId = val
        this.loadCategories(val)
        this.loadData()
      }
    }
  },
  methods: {
    loadEvents() {
      getDragonEventList({ page: 1, pageSize: 200 }).then(res => {
        this.eventOptions = res.data.list || []
      })
    },
    loadCategories(eventId) {
      if (!eventId) {
        this.categoryOptions = []
        return
      }
      getDragonCategoryList({ eventId }).then(res => {
        this.categoryOptions = res.data.list || []
      })
    },
    handleEventChange(value) {
      this.query.categoryId = undefined
      this.loadCategories(value)
    },
    loadData() {
      this.loading = true
      getDragonRegistrationList({
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        eventId: this.query.eventId,
        categoryId: this.query.categoryId,
        auditStatus: this.query.auditStatus
      }).then(res => {
        this.data = res.data.list
        this.pagination.total = res.data.total
      }).finally(() => {
        this.loading = false
      })
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.loadData()
    },
    openAudit(record) {
      this.auditForm = {
        registrationId: record.id,
        teamId: record.teamId,
        teamName: record.teamName,
        categoryName: record.categoryName,
        teamPhotoUrl: record.teamPhotoUrl,
        bibNo: record.bibNo,
        auditStatus: record.auditStatus === 2 ? 2 : 1,
        auditRemark: record.auditRemark,
        seatMembers: record.seatMembers || [],
        alternateMembers: record.alternateMembers || [],
        staffMembers: record.staffMembers || [],
        snapshotMemberIds: record.snapshotMemberIds || [],
        eligibleMembers: record.eligibleMembers || [],
        seatRoles: record.seatRoles || []
      }
      this.memberAddSelection = undefined
      this.boatRows = []
      this.dragOverAlt = null
      this.dragOverStaff = null
      this.seatPickMode = 'seat'
      this.seatPickStaffRole = ''
      this.unlimitedAlternates = record.substituteMode === 'UNLIMITED'
      this.maxAlternates = this.unlimitedAlternates
        ? Math.max((record.alternateMembers || []).length + 1, 1)
        : Math.max(Number(record.maxAlternateParticipants) || 0, 0)
      this.loadTeamMembers(record.teamId)
      // 船型和替补规则以报名所属组别为准，同一赛事可并存大龙和小龙。
      this.boatRows = this.buildBoatRows(record.boatSeatRows, 2)
      this.visible = true
    },
    // 构建船型座位网格（船头→船尾，单双座），与领队报名可视化一致
    buildBoatRows(seatRows, legacyLayout) {
      const counts = Array.isArray(seatRows) ? seatRows.map(Number).filter(n => n === 1 || n === 2) : []
      const legacy = Number(legacyLayout) === 1 ? 1 : 2
      if (!counts.length) {
        for (let i = 0; i < 4; i += 1) counts.push(legacy)
      }
      const rows = []
      let seatOrder = 1
      counts.forEach(count => {
        const seats = []
        for (let offset = 0; offset < count; offset += 1) {
          seats.push({
            seatOrder,
            sideText: count === 1 ? '中间' : offset === 0 ? '左侧' : '右侧',
            member: null
          })
          seatOrder += 1
        }
        rows.push({ rowNo: rows.length + 1, seats })
      })
      return rows
    },
    // 随队人员（领队/教练，is_alternate=2）
    staffMembers() {
      return this.auditForm.staffMembers || []
    },
    staffLeader() {
      return this.staffMembers().find(x => Number(x.isLeader) === 1) || null
    },
    staffCoach() {
      return this.staffMembers().find(x => Number(x.isLeader) !== 1 && String(x.racePosition || '') === '教练') || null
    },
    otherStaffMembers() {
      return this.staffMembers().filter(x => !(Number(x.isLeader) === 1) && String(x.racePosition || '') !== '教练')
    },
    // 替补格子按组别规则生成；不限额时始终多留一个空位用于继续添加。
    alternateSlots() {
      const list = this.auditForm.alternateMembers || []
      const count = this.unlimitedAlternates
        ? Math.max(list.length + 1, Number(this.maxAlternates) || 0, 1)
        : Math.max(Number(this.maxAlternates) || 0, list.length)
      const slots = []
      for (let i = 1; i <= count; i += 1) {
        slots.push({ slotNo: i, member: list[i - 1] || null })
      }
      return slots
    },
    seatPickTitle() {
      if (this.seatPickMode === 'alt') return '选择替补成员（替补' + (this.seatPickOrder || '') + '）'
      if (this.seatPickMode === 'staff') return '选择' + (this.seatPickStaffRole === 'leader' ? '领队' : '教练')
      return '为 ' + this.seatPickOrder + ' 号空座位选择队员'
    },
    // 座位关联报名成员（船型网格可视化），方法调用保证每次渲染取最新数据
    auditBoatRows() {
      const memberMap = {}
      ;(this.auditForm.seatMembers || []).forEach(m => {
        memberMap[Number(m.seatOrder)] = m
      })
      const rows = (this.boatRows || []).map(row => ({
        ...row,
        seats: row.seats.map(seat => ({
          ...seat,
          member: memberMap[Number(seat.seatOrder)] || null
        }))
      }))
      return rows
    },
    // 超级管理员在座位图上编辑：替换/移除座位成员
    onMemberDragStart(e, member) {
      this.dragMemberId = member.id
      this.dragMemberName = member.memberName
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(member.id))
    },
    onSeatDrop(e, seat) {
      this.dragOverSeat = null
      const memberId = Number(e.dataTransfer.getData('text/plain')) || this.dragMemberId
      if (!memberId) return
      const member = (this.teamMemberOptions || []).find(m => Number(m.id) === Number(memberId))
      if (!member) return
      if (!this.isInSnapshot(memberId)) {
        this.$message.warning('报名已审核通过，未报名成员不可加入')
        return
      }
      // 校验：目标座位是否已有人
      if (seat.member) {
        this.$message.warning(`座位 ${seat.seatOrder} 已被 ${seat.member.memberName} 占用，请先移除`)
        return
      }
      // 校验：该队员是否已在座舱/替补/随队
      const inSeat = (this.auditForm.seatMembers || []).some(m => Number(m.memberId) === Number(memberId))
      const inAlt = (this.auditForm.alternateMembers || []).some(m => Number(m.memberId) === Number(memberId))
      const inStaff = this.staffMembers().some(m => Number(m.memberId) === Number(memberId))
      if (inSeat) {
        this.$message.warning(`${member.memberName} 已在座舱，请先在座位图/名单中移除`)
        return
      }
      if (inAlt) {
        this.$message.warning(`${member.memberName} 是替补成员，请先在名单中移除`)
        return
      }
      if (inStaff) {
        this.$message.warning(`${member.memberName} 是随队人员（领队/教练），请先移除`)
        return
      }
      addDragonRegistrationMember({
        registrationId: this.auditForm.registrationId,
        teamMemberId: memberId,
        isAlternate: 0,
        seatOrder: seat.seatOrder
      }).then(() => {
        this.$message.success(`${member.memberName} 已上 ${seat.seatOrder} 号座舱`)
        this.refreshAuditData()
      }).catch(err => {
        this.$message.error(err.message || '上座失败')
      })
    },
    seatSelect(seat) {
      const current = seat.member
      if (!current) {
        // 空座位：从可用队伍成员中精确选择上座
        this.seatPickMode = 'seat'
        this.seatPickOrder = seat.seatOrder
        this.seatPickVisible = true
        return
      }
      this.$confirm({
        title: `操作 ${current.seatOrder}号座舱 · ${current.memberName}`,
        content: '移除后可点击该空座位重新选择队员上座。',
        okText: '移除该成员',
        okType: 'danger',
        cancelText: '取消',
        onOk: () => this.handleMemberRemove(current)
      })
    },
    // 拖入替补格子
    onAltDrop(e) {
      this.dragOverAlt = null
      const memberId = Number(e.dataTransfer.getData('text/plain')) || this.dragMemberId
      if (!memberId) return
      const member = (this.teamMemberOptions || []).find(m => Number(m.id) === Number(memberId))
      if (!member) return
      if (!this.isInSnapshot(memberId)) {
        this.$message.warning('报名已审核通过，未报名成员不可加入')
        return
      }
      this.addAsTarget(member, { isAlternate: 1, successText: member.memberName + ' 已加入替补' })
    },
    // 拖入领队/教练格子
    onStaffDrop(e, role) {
      this.dragOverStaff = null
      const memberId = Number(e.dataTransfer.getData('text/plain')) || this.dragMemberId
      if (!memberId) return
      const member = (this.teamMemberOptions || []).find(m => Number(m.id) === Number(memberId))
      if (!member) return
      if (!this.isInSnapshot(memberId)) {
        this.$message.warning('报名已审核通过，未报名成员不可加入')
        return
      }
      if (role === 'leader' && this.staffLeader()) {
        this.$message.warning('已有领队：' + this.staffLeader().memberName + '，请先移除')
        return
      }
      if (role === 'coach' && this.staffCoach()) {
        this.$message.warning('已有教练：' + this.staffCoach().memberName + '，请先移除')
        return
      }
      this.addAsTarget(member, { isAlternate: 2, staffRole: role, successText: member.memberName + ' 已设为' + (role === 'leader' ? '领队' : '教练') })
    },
    // 点击空格子选择成员
    altSlotClick(slot) {
      if (slot.member) {
        this.$confirm({
          title: '操作替补' + slot.slotNo + ' · ' + slot.member.memberName,
          content: '移除该替补成员？',
          okText: '移除',
          okType: 'danger',
          onOk: () => this.handleMemberRemove(slot.member)
        })
        return
      }
      this.seatPickMode = 'alt'
      this.seatPickOrder = slot.slotNo
      this.seatPickVisible = true
    },
    staffSlotClick(role) {
      const current = role === 'leader' ? this.staffLeader() : this.staffCoach()
      if (current) {
        this.$confirm({
          title: '操作' + (role === 'leader' ? '领队' : '教练') + ' · ' + current.memberName,
          content: '移除该随队人员？',
          okText: '移除',
          okType: 'danger',
          onOk: () => this.handleMemberRemove(current)
        })
        return
      }
      this.seatPickMode = 'staff'
      this.seatPickStaffRole = role
      this.seatPickVisible = true
    },
    // 按目标类型添加成员（座舱/替补/领队教练），带互斥校验
    addAsTarget(member, target) {
      const memberId = member.id
      const inSeat = (this.auditForm.seatMembers || []).some(m => Number(m.memberId) === Number(memberId))
      const inAlt = (this.auditForm.alternateMembers || []).some(m => Number(m.memberId) === Number(memberId))
      const inStaff = this.staffMembers().some(m => Number(m.memberId) === Number(memberId))
      if (target.isAlternate === 1) {
        if (!this.unlimitedAlternates && (this.auditForm.alternateMembers || []).length >= this.maxAlternates) {
          this.$message.warning(this.maxAlternates > 0 ? `本组别替补最多${this.maxAlternates}人` : '本组别不设替补')
          return
        }
        if (inSeat || inAlt || inStaff) {
          this.$message.warning(`${member.memberName} 已在座舱/替补/随队人员中，请先移除`)
          return
        }
      } else if (target.isAlternate === 2) {
        if (inSeat || inAlt || inStaff) {
          this.$message.warning(`${member.memberName} 已在报名名单中，请先移除`)
          return
        }
      }
      addDragonRegistrationMember({
        registrationId: this.auditForm.registrationId,
        teamMemberId: memberId,
        isAlternate: target.isAlternate,
        staffRole: target.staffRole,
        seatOrder: target.seatOrder
      }).then(() => {
        this.$message.success(target.successText || '成员已添加')
        this.refreshAuditData()
      }).catch(err => {
        this.$message.error(err.message || '添加失败')
      })
    },
    // 空座位选择成员上座
    seatPickMember(member) {
      if (this.seatPickMode === 'alt') {
        this.seatPickVisible = false
        this.addAsTarget(member, { isAlternate: 1, successText: member.memberName + ' 已加入替补' })
        return
      }
      if (this.seatPickMode === 'staff') {
        const role = this.seatPickStaffRole
        this.seatPickVisible = false
        this.addAsTarget(member, { isAlternate: 2, staffRole: role, successText: member.memberName + ' 已设为' + (role === 'leader' ? '领队' : '教练') })
        return
      }
      addDragonRegistrationMember({
        registrationId: this.auditForm.registrationId,
        teamMemberId: member.id,
        isAlternate: 0,
        seatOrder: this.seatPickOrder
      }).then(() => {
        this.$message.success(`${member.memberName} 已上 ${this.seatPickOrder} 号座舱`)
        this.seatPickVisible = false
        this.refreshAuditData()
      }).catch(err => {
        this.$message.error(err.message || '上座失败')
      })
    },
    loadTeamMembers() {
      this.teamMemberOptions = this.auditForm.eligibleMembers || []
      this.teamMemberLoading = false
    },
    // 报名快照成员集合（提交时成员），用于审核后"快照内可调、快照外禁加"
    isInSnapshot(id) {
      return (this.auditForm.snapshotMemberIds || []).map(Number).includes(Number(id))
    },
    // 队伍全部成员 + 报名状态（N号座舱/替补/未上座/未报名）
    membersWithStatus() {
      const seatMap = {}
      ;(this.auditForm.seatMembers || []).forEach(m => {
        seatMap[Number(m.memberId)] = { status: 'seat', seatOrder: m.seatOrder, member: m }
      })
      const altSet = new Set((this.auditForm.alternateMembers || []).map(m => Number(m.memberId)))
      return (this.teamMemberOptions || []).filter(m => this.isInSnapshot(m.id)).map(m => {
        const seat = seatMap[Number(m.id)]
        const inSnap = this.isInSnapshot(m.id)
        if (seat) return { ...m, _status: `座舱${seat.seatOrder}号`, _statusType: 'seat', _snapshot: seat.member, _inSnapshot: inSnap }
        if (altSet.has(Number(m.id))) return { ...m, _status: '替补', _statusType: 'alt', _inSnapshot: inSnap }
        if (!inSnap) return { ...m, _status: '未报名（审核后不可加入）', _statusType: 'foreign' }
        return { ...m, _status: '未上座', _statusType: 'idle', _inSnapshot: inSnap }
      })
    },
    // 座位选人列表（已占座/未报名成员不可选）
    seatPickMembers() {
      const usedIds = new Set([
        ...(this.auditForm.seatMembers || []).map(m => Number(m.memberId)),
        ...(this.auditForm.alternateMembers || []).map(m => Number(m.memberId)),
        ...(this.auditForm.staffMembers || []).map(m => Number(m.memberId))
      ])
      return (this.teamMemberOptions || []).filter(m => this.isInSnapshot(m.id) &&
        (this.seatPickMode === 'staff' ? (Number(m.isLeader) === 1 || m.racePosition === '教练') : (Number(m.isLeader) !== 1 && m.racePosition !== '教练'))).map(m => ({
        ...m,
        _used: usedIds.has(Number(m.id)),
        _inSnapshot: this.isInSnapshot(m.id)
      }))
    },
    submitMemberAdd() {
      if (!this.memberAddSelection) return
      if (this.memberAddIsAlternate === 1
        && !this.unlimitedAlternates
        && (this.auditForm.alternateMembers || []).length >= this.maxAlternates) {
        this.$message.warning(this.maxAlternates > 0 ? `本组别替补最多${this.maxAlternates}人` : '本组别不设替补')
        return
      }
      addDragonRegistrationMember({
        registrationId: this.auditForm.registrationId,
        teamMemberId: this.memberAddSelection,
        isAlternate: this.memberAddIsAlternate
      }).then(() => {
        this.$message.success('成员已添加')
        this.memberAddSelection = undefined
        this.refreshAuditData()
      }).catch(err => {
        this.$message.error(err.message || '添加失败')
      })
    },
    handleMemberRemove(item) {
      removeDragonRegistrationMember({ registrationMemberId: item.registrationMemberId }).then(() => {
        this.$message.success('成员已移除')
        this.refreshAuditData()
      }).catch(err => {
        this.$message.error(err.message || '移除失败')
      })
    },
    refreshAuditData() {
      getDragonRegistrationList({
        page: 1,
        pageSize: 10,
        eventId: this.query.eventId
      }).then(res => {
        const reg = (res.data.list || []).find(r => r.id === this.auditForm.registrationId)
        if (reg) {
          this.auditForm.seatMembers = reg.seatMembers || []
          this.auditForm.alternateMembers = reg.alternateMembers || []
          this.auditForm.staffMembers = reg.staffMembers || []
          this.auditForm.snapshotMemberIds = reg.snapshotMemberIds || []
          this.auditForm.eligibleMembers = reg.eligibleMembers || []
          this.loadTeamMembers(reg.teamId)
        }
      })
      this.loadData()
    },
    previewPhoto(url) {
      const image = new Image()
      image.src = url
      const win = window.open('')
      if (win) {
        win.document.write(`<img src="${url}" style="max-width:100%;" />`)
      }
    },
    openMemberEdit(item) {
      this.memberForm = {
        registrationMemberId: item.registrationMemberId,
        memberName: item.memberName || '',
        gender: item.gender || undefined,
        mobile: item.mobile || '',
        idCardNo: item.idCardNo || '',
        birthYear: item.birthYear || undefined,
        residenceProvince: item.residenceProvince || '',
        residenceCity: item.residenceCity || '',
        residenceDistrict: item.residenceDistrict || '',
        heightCm: item.heightCm || undefined,
        weightKg: item.weightKg || undefined,
        clothingSize: item.clothingSize || '',
        participationHistory: item.participationHistory || ''
      }
      this.memberVisible = true
    },
    submitMemberEdit() {
      if (!this.memberForm.registrationMemberId) {
        this.$message.warning('缺少成员信息')
        return
      }
      updateDragonRegistrationMember(this.memberForm).then(() => {
        this.$message.success('资料已更新')
        this.memberVisible = false
        this.loadData()
      })
    },
    submitAudit() {
      auditDragonRegistration(this.auditForm).then(() => {
        this.$message.success('审核完成')
        this.visible = false
        if (!this.dialogOnly) this.loadData()
        this.$emit('updated')
      })
    },
    auditTeamPass() {
      this.teamAuditing = true
      auditDragonTeam({
        teamId: this.auditForm.teamId,
        auditStatus: 1,
        auditRemark: '报名审核时一并通过'
      }).then(() => {
        this.$message.success('队伍已通过审核')
        this.auditForm.teamAuditStatus = 1
      }).finally(() => {
        this.teamAuditing = false
      })
    },
    handleDelete(id) {
      deleteDragonRegistration({ registrationId: id }).then(() => {
        this.$message.success('删除成功')
        this.loadData()
      })
    }
  },
  computed: {
    teamAuditTagText() {
      const status = this.auditForm.teamAuditStatus
      if (status === 1) return '队伍已通过'
      if (status === 0) return '队伍待审核'
      if (status === 2) return '队伍已拒绝'
      return ''
    },
    teamAuditTagColor() {
      const status = this.auditForm.teamAuditStatus
      if (status === 1) return 'green'
      if (status === 0) return 'orange'
      if (status === 2) return 'red'
      return 'default'
    }
  }
}
</script>

<style scoped>
.seat-member-list { padding: 8px 12px; border-radius: 4px; background: #f5faf9; }
.seat-member-item { display: flex; justify-content: space-between; gap: 16px; padding: 6px 0; border-bottom: 1px solid #e8f0ef; }
.seat-member-item:last-child { border-bottom: 0; }

/* 审核弹窗：左资料右座位图 */
.audit-layout {
  display: flex;
  gap: 20px;
  max-height: 62vh;
  min-height: 480px;
}

.audit-left {
  flex: 1;
  min-width: 0;
  border-right: 1px solid #E4EDF2;
  padding-right: 16px;
  overflow-y: auto;
}

.audit-right {
  width: 340px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.audit-panel-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #16232F;
  padding-bottom: 10px;
  border-bottom: 1px solid #E4EDF2;
}

.audit-panel-count {
  font-size: 12px;
  font-weight: 400;
  color: #7A8A9A;
}

.audit-member-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 10px;
}

.audit-member-card {
  border: 1px solid #E4EDF2;
  border-radius: 10px;
  padding: 10px 12px;
  background: #FBFDFD;
}

.audit-member-card.audit-foreign {
  background: #F5F6F7;
  border: 1px dashed #D8DCE0;
  color: #8A949E;
}
.audit-member-card.audit-foreign .audit-member-name,
.audit-member-card.audit-foreign .audit-member-fields {
  color: #8A949E;
}
.audit-member-card.audit-foreign .audit-member-fields span {
  color: #8A949E;
}

.audit-member-card.audit-alt {
  background: #FFFAF0;
  border-color: #F5E6C8;
}

.audit-member-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.audit-member-name {
  font-size: 14px;
  font-weight: 600;
  color: #16232F;
}

.audit-member-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  font-size: 12px;
  color: #64748B;
}

.audit-member-history {
  width: 100%;
  color: #7A8A9A;
}

.audit-team-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 15px;
}

.audit-team-photo {
  max-width: 100%;
  max-height: 80px;
  border-radius: 8px;
  margin-top: 4px;
}

.audit-boat {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px 0;
  overflow-y: auto;
  background: #F7FAFA;
  border-radius: 10px;
}

.audit-staff {
  width: 88%;
  padding: 8px 12px;
  background: #F4F7FB;
  border: 1px solid #E3EAF2;
  border-radius: 8px;
  font-size: 13px;
}
.audit-staff-title {
  color: #64748B;
  font-size: 12px;
  margin-bottom: 6px;
}
.audit-staff-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  color: #16232F;
}

.audit-boat-end {
  font-size: 11px;
  color: #7A8A9A;
  letter-spacing: 3px;
  background: #fff;
  border-radius: 6px;
  padding: 2px 18px;
}

.audit-boat-row {
  display: flex;
  gap: 10px;
  width: 88%;
}
.audit-boat-row.single {
  justify-content: center;
}
.audit-boat-seat {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #E4EDF2;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: all .15s;
}
.audit-boat-seat:hover {
  border-color: #4ECDC4;
  background: #F0FBFA;
}
.audit-boat-seat.empty {
  border-style: dashed;
  color: #bbb;
}
.audit-boat-seat.dragover {
  border-color: #4ECDC4;
  background: #E6F7F5;
  box-shadow: 0 0 0 2px rgba(78, 205, 196, 0.3);
}
.audit-boat-row.double .audit-boat-seat {
  flex: 0 0 44%;
}
.audit-boat-row.single .audit-boat-seat {
  flex: 0 0 44%;
}

.audit-boat-pos {
  color: #4ECDC4;
  font-weight: 600;
  white-space: nowrap;
}

.audit-boat-name {
  color: #16232F;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.seat-pick-inline {
  margin-top: 10px;
  padding: 10px 14px;
  background: #FBFDFD;
  border: 1px solid #DCEFEC;
  border-radius: 10px;
}
.seat-pick-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-weight: 600;
  color: #16232F;
}

.audit-boat-alt {
  width: 88%;
  border-top: 1px dashed #D8E4EA;
  padding-top: 6px;
  font-size: 12px;
  color: #64748B;
}

.audit-boat-alt-title {
  color: #B08A3E;
  margin-bottom: 4px;
}
.audit-staff-slots {
  width: 88%;
  display: flex;
  gap: 10px;
  margin-bottom: 6px;
}
.audit-staff-slot {
  flex: 1;
  border: 1px dashed #C7D3DE;
  border-radius: 8px;
  padding: 6px 10px;
  min-height: 42px;
  background: #FAFCFE;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.audit-staff-slot-empty {
  border-style: dashed;
}
.audit-staff-slot.dragover,
.audit-boat-alt-slot.dragover {
  border-color: #4ECDC4;
  background: #EFFAF8;
}
.audit-staff-slot-label {
  color: #7A8A9A;
  font-size: 12px;
  flex-shrink: 0;
}
.audit-staff-slot-body {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #16232F;
}
.audit-staff-slot-placeholder {
  color: #B8C4CE;
  font-size: 12px;
}
.audit-boat-alt-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.audit-boat-alt-slot {
  flex: 0 0 calc(50% - 4px);
  border: 1px dashed #C7D3DE;
  border-radius: 8px;
  padding: 6px 10px;
  min-height: 38px;
  background: #FAFCFE;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.audit-boat-alt-slot-empty {
  border-style: dashed;
}
.audit-boat-alt-slot-label {
  color: #B08A3E;
  font-size: 12px;
  flex-shrink: 0;
}
.audit-boat-alt-slot-body {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #16232F;
}

.audit-form {
  border-top: 1px solid #E4EDF2;
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.audit-form-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.audit-form-label {
  width: 62px;
  flex-shrink: 0;
  font-size: 13px;
  color: #7A8A9A;
}
</style>
